/* One page, from diagnosis to finished copy.

   Not a worksheet and then a rewriter. Reading a finding, deciding it is
   worth adding, and getting the copy are one task, and splitting them across
   two screens would mean carrying the context between them.

   The order on screen is the order of the decision: what is wrong, what can
   be fixed now, what has to be asked first. A page missing something the
   Vault already holds is a writing job; a page missing something nobody has
   answered needs an interview, and sending that to a rewriter produces
   padding — which is what the effort score exists to catch. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Textarea } from '@/components/ui';
import { ScoreDetail } from '@/components/ScoreDetail';
import { Icon } from '@/lib/icons';
import {
  getPageDetail, rewritePage, askFaqPage, requestPageInterview, rescanPage,
  type PageFinding, type RewriteResult, type RewriteChange,
} from '@/data/client';

const OK = 'var(--ok, #15803d)';
const WARN = 'var(--warn, #b45309)';
const BAD = 'var(--danger, #b91c1c)';

const KIND_TONE: Record<RewriteChange['kind'], string> = {
  added: OK, extended: OK, replaced: WARN, removed: BAD, unchanged: 'var(--text-muted)',
};

/* Days while that is still a prompt to chase, a date once it stops being
   about elapsed time. */
function since(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (!Number.isFinite(days)) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 45) return `${days} days ago`;
  return `on ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}


interface AskItem { key: number; question: string; live: boolean }

/* One row per question, with a one-click way out of the wrong group. The
   routing is a model's judgement and it will sometimes put a sentence-long
   answer in the interview pile; without this, the only options would be losing
   the question or sending it the wrong way. */
function QuestionList({ items, picked, onToggle, moveLabel, onMove }: {
  items: AskItem[];
  picked: Set<number>;
  onToggle: (key: number) => void;
  moveLabel: string;
  onMove: (key: number) => void;
}) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((it) => (
        <li key={it.key} style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13 }}>
          <input type="checkbox" checked={picked.has(it.key)} onChange={() => onToggle(it.key)} style={{ marginTop: 3 }} />
          <span>
            {it.question}
            {it.live && <span className="dash-muted"> — worth a live conversation</span>}
            <button type="button" onClick={() => onMove(it.key)}
              style={{ background: 'none', border: 'none', padding: '0 0 0 6px', cursor: 'pointer', color: 'var(--triton-blue)', fontSize: 12 }}>
              {moveLabel}
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Changes({ r }: { r: RewriteResult }) {
  if (!r.summary) return null;
  const s = r.summary;
  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ fontSize: 13, margin: '0 0 6px' }}>
        <strong style={{ color: OK }}>{s.added} added</strong>
        {s.extended > 0 && <> · {s.extended} extended</>}
        {s.replaced > 0 && <> · <span style={{ color: WARN }}>{s.replaced} rewritten</span></>}
        {s.removed > 0 && <> · <strong style={{ color: BAD }}>{s.removed} removed</strong></>}
        {' · '}{s.unchanged} untouched
      </p>
      {/* Only the two that can lose somebody's words. Making a person read
          every addition would turn the review into a rubber stamp. */}
      {s.needsReview.length === 0
        ? <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>Nothing was removed or rewritten — everything here is new copy.</p>
        : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {s.needsReview.map((c, i) => (
              <li key={i} style={{ fontSize: 12, padding: '6px 0', borderTop: '1px solid var(--border, #e5e7eb)' }}>
                <strong style={{ color: KIND_TONE[c.kind] }}>{c.kind === 'removed' ? 'Removed' : 'Rewritten'}</strong>
                {c.before && <div style={{ textDecoration: c.kind === 'removed' ? 'line-through' : 'none', opacity: 0.75 }}>{c.before}</div>}
                {c.after && <div>{c.after}</div>}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

export function PageWorksheet({ route, onBack }: { route: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['pageDetail', route], queryFn: () => getPageDetail(route) });
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<'notes' | 'file'>('notes');
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<RewriteResult | null>(null);
  const [sending, setSending] = useState<'' | 'faq' | 'interview'>('');
  const [sent, setSent] = useState('');
  const [moved, setMoved] = useState<Record<number, 'faq' | 'interview'>>({});
  const [foldIn, setFoldIn] = useState(true);
  const [rescanning, setRescanning] = useState(false);

  const rescan = async () => {
    setRescanning(true); setErr(''); setSent('');
    try {
      await rescanPage(route);
      /* Both: the detail is what this screen reads, and the list's ranks and
         badges are now wrong too. */
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['pageDetail', route] }),
        qc.invalidateQueries({ queryKey: ['pageHealth'] }),
      ]);
      setSent('Re-read. The findings below are up to date with the Vault.');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not re-read that page.'); }
    finally { setRescanning(false); }
  };
  const [unticked, setUnticked] = useState<Set<number>>(new Set());

  /* Ticked by default: the model has already decided these belong in this
     group, and the common action is confirming rather than choosing. So the
     state tracks what was turned OFF. */
  const unanswered = data?.unanswered ?? [];
  const ask = { faq: [] as AskItem[], interview: [] as AskItem[] };
  unanswered.forEach((u, key) => {
    const side = moved[key] ?? (u.route === 'faq' ? 'faq' : 'interview');
    ask[side].push({ key, question: u.question, live: u.route === 'live' });
  });
  const pickOf = (items: AskItem[]) => new Set(items.filter((i) => !unticked.has(i.key)).map((i) => i.key));
  const faqPick = pickOf(ask.faq);
  const ivPick = pickOf(ask.interview);
  /* On by default, and only ever offered when there is an interview to fold
     into: a page whose gaps are all discrete facts should get an email, not a
     request for somebody's afternoon. */
  const foldingIn = foldIn && ask.interview.length > 0 && ask.faq.length > 0;
  const ivTotal = ivPick.size + (foldingIn ? faqPick.size : 0);

  const flip = (key: number) => setUnticked((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const toggleFaq = flip;
  const toggleIv = flip;
  const moveTo = (key: number, side: 'faq' | 'interview') => setMoved((m) => ({ ...m, [key]: side }));

  const send = async (how: 'faq' | 'interview') => {
    const chosen = how === 'faq'
      ? faqPick
      : new Set([...ivPick, ...(foldingIn ? faqPick : [])]);
    const keys = [...chosen].filter((k) => unanswered[k]?.question);
    if (!keys.length) return;
    setSending(how); setErr(''); setSent('');
    try {
      if (how === 'faq') {
        const r = await askFaqPage(route, keys.map((k) => unanswered[k].question));
        setSent(`Sent ${r.sent} question${r.sent === 1 ? '' : 's'} to the client${r.drafted ? `, ${r.drafted} with a draft answer from the Vault` : ''}.`);
      } else {
        /* The destination travels with each question. A folded-in quick one is
           still meant to become a page FAQ, and after the interview it is
           drafted from the transcript — without this it would stay in there. */
        const folded = faqPick.size && foldingIn ? ` The ${faqPick.size} quick one${faqPick.size === 1 ? '' : 's'} will come back as page FAQs.` : '';
        const r = await requestPageInterview({
          page: route,
          questions: keys.map((k) => ({
            q: unanswered[k].question,
            route: (moved[k] ?? unanswered[k].route) === 'faq' ? 'faq' as const : unanswered[k].route,
          })),
        });
        setSent(`Interview requested — "${r.title}" is on the client's dashboard.${folded}`);
      }
      await qc.invalidateQueries({ queryKey: ['pageDetail', route] });
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not send those.'); }
    finally { setSending(''); }
  };

  const missing: PageFinding[] = data?.missing ?? [];
  const toggle = (i: number) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const run = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      setResult(await rewritePage({
        page: route, mode,
        body: mode === 'file' ? pasted : undefined,
        additions: [...picked].map((i) => ({ what: missing[i].what, source: missing[i].source?.title ?? '' })),
      }));
    } catch (e) { setErr(e instanceof Error ? e.message : 'Rewrite failed'); }
    finally { setBusy(false); }
  };

  if (isLoading) return <Card padding="lg"><p className="dash-muted">Loading…</p></Card>;
  if (!data) return <Card padding="lg"><p className="dash-muted">This page hasn&rsquo;t been scanned yet.</p></Card>;

  /* The page's own name from the site map, the route beside it, and the
     crawled <title> as what the page calls itself. What the page is for —
     keywords, brief, the service's interview readiness — sits with the score
     so a finding is read against the intent. */
  const r = data.readiness;
  const header = (
    <Card padding="lg">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 30, fontWeight: 700, color: data.rank === null ? 'var(--text-muted)' : data.rank >= 7 ? OK : data.rank >= 4 ? WARN : BAD }}>
          {data.rank ?? '—'}
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>
            {data.name || data.title || data.route}
            {' '}<code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 400 }}>{data.route}</code>
            {data.pageType && <span className="smb__type" style={{ marginLeft: 8 }}>{data.pageType}</span>}
          </h2>
          <p className="dash-muted" style={{ margin: 0, fontSize: 12 }}>
            {[
              data.name && data.title && data.title !== data.name ? `calls itself "${data.title}"` : '',
              data.effort?.topic ? `reads as ${data.effort.topic.toLowerCase()}` : '',
              data.keywords ? `for ${data.keywords}` : '',
            ].filter(Boolean).join(' · ')}
          </p>
          {data.brief && <p style={{ margin: '4px 0 0', fontSize: 13 }}>{data.brief}</p>}
        </div>
        {r && (
          <span title={`${r.service}: ${r.covered.length} of ${r.covered.length + r.open.length} interview slots covered`}>
            <Badge variant={r.state === 'ready' ? 'success' : 'warning'}>
              {r.state === 'ready' ? 'Interviews ready' : r.state === 'thin' ? 'Thin interviews' : 'No interviews'}
            </Badge>
          </span>
        )}
        {data.stale && <Badge variant="neutral">New Vault material since this was scored</Badge>}
      </div>
      {data.source !== 'planned' && (
        <div style={{ marginTop: 10 }}>
          <ScoreDetail effort={data.effort} facts={data.facts} proof={data.proof} />
        </div>
      )}
    </Card>
  );

  /* A page that is not written yet has nothing to score. Its worksheet is
     the plan: what it is for, what the service's interviews still lack, and
     the questions open for it on the site map. */
  if (data.source === 'planned') {
    const open = data.questions ?? [];
    return (
      <div className="dash-stack">
        <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> Back to page health</button>
        {header}
        <Card padding="lg">
          <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>Not written yet</h3>
          <p className="dash-muted" style={{ fontSize: 13, margin: '0 0 8px' }}>
            This page is {data.status === 'scaffolded' ? 'scaffolded in the repo but still a template' : 'planned on the site map'}. It will be scored once it is built and live.
          </p>
          {r && r.open.length > 0 && (
            <p style={{ fontSize: 13, margin: '0 0 8px' }}>
              Still open for <strong>{r.service}</strong>: {r.open.join(', ')}.
            </p>
          )}
          {open.length > 0
            ? (
              <>
                <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px' }}>Questions on the site map ({open.length})</p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {open.map((q, i) => (
                    <li key={i} style={{ fontSize: 13, margin: '3px 0' }}>
                      {q.q}
                      <span className="dash-muted"> — {q.state === 'sent' ? 'asked, waiting' : q.routeTo === 'faq' ? 'a quick answer' : 'needs an interview'}</span>
                    </li>
                  ))}
                </ul>
              </>
            )
            : <p className="dash-muted" style={{ fontSize: 13, margin: 0 }}>No open questions for it. Write them from the site map under contentIQ → Vault.</p>}
        </Card>
      </div>
    );
  }

  return (
    <div className="dash-stack">
      <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> Back to page health</button>

      {header}

      {/* What is already out with the client, and what has come back.

          The reason this is worth a panel: without it somebody re-asks
          questions answered three weeks ago, or waits for answers nobody
          requested. And the last line is the one that matters — it is the
          signal that everything asked for has landed and the page can be
          rewritten with the whole picture rather than piecemeal. */}
      {((data.asked ?? []).length > 0 || (data.interviews ?? []).length > 0 || data.stale) && (
        <Card padding="lg">
          <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>Already asked</h3>
          {(data.asked ?? []).map((a, i) => {
            const outstanding = a.total - a.answered - a.declined;
            return (
              <p key={i} style={{ fontSize: 13, margin: '3px 0' }}>
                {a.total} question{a.total === 1 ? '' : 's'} sent {since(a.sentAt)} ·{' '}
                <strong style={{ color: outstanding === 0 ? OK : WARN }}>
                  {a.answered} answered{outstanding > 0 ? `, ${outstanding} outstanding` : ''}
                </strong>
                {a.declined > 0 && <span className="dash-muted"> · {a.declined} declined</span>}
              </p>
            );
          })}
          {(data.interviews ?? []).map((t) => (
            <p key={t.id} style={{ fontSize: 13, margin: '3px 0' }}>
              Interview requested {since(t.createdAt)} — <strong style={{ color: t.status === 'used' ? OK : WARN }}>
                {t.status === 'used' ? 'done' : t.status === 'dismissed' ? 'dismissed' : 'not started'}
              </strong>
              <span className="dash-muted"> · {t.title}</span>
            </p>
          ))}
          {/* The findings below are frozen at the last scan. Answers that
              landed since are invisible until this is pressed, and sending
              somebody back to the list — where unscanned pages queue ahead of
              stale ones — would mean waiting on forty pages to see whether the
              answers they chased actually filled the gap. */}
          {data.stale && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <Button variant="primary" size="sm" disabled={rescanning} onClick={rescan}
                iconLeft={<Icon name="refresh-cw" size={14} />}>
                {rescanning ? 'Re-reading…' : 'Re-read this page'}
              </Button>
              <span style={{ fontSize: 13, color: OK }}>
                New material has landed in the Vault — the findings below are from before it.
              </span>
            </div>
          )}
        </Card>
      )}

      {(data.conflicts ?? []).length > 0 && (
        <Card padding="lg">
          <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>Contradictions</h3>
          {data.conflicts!.map((cf, i) => (
            <p key={i} style={{ fontSize: 13, margin: '4px 0', color: BAD }}>{cf.what}</p>
          ))}
        </Card>
      )}

      <Card padding="lg">
        <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>In the Vault, not on this page</h3>
        <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          Tick what belongs on this page. Each one is already on record, so adding it invents nothing.
        </p>
        {missing.length === 0
          ? <p className="dash-muted" style={{ fontSize: 13 }}>Nothing — this page spends what&rsquo;s on record.</p>
          : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {missing.map((m, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: i ? '1px solid var(--border, #e5e7eb)' : 'none' }}>
                  <input type="checkbox" checked={picked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>
                    {m.what}
                    {m.why && <span className="dash-muted"> — {m.why}</span>}
                    <div className="dash-muted" style={{ fontSize: 12 }}>
                      from {m.source ? `[${m.source.kind}] ${m.source.title || m.source.id}` : '(source not resolved)'}
                    </div>
                  </span>
                </li>
              ))}
            </ul>
          )}
      </Card>

      {/* Split by how each question is best asked, because the two go to
          different places and each goes as ONE request. The model already
          routed them; this stops that judgement being thrown away and a client
          getting two emails about the same page. */}
      {ask.faq.length > 0 && (
        <Card padding="lg">
          <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>Ask the client these</h3>
          <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
            Discrete facts they can type in a sentence or two. They come back as FAQs on this page
            and as Vault material at the same time.
          </p>
          <QuestionList items={ask.faq} picked={faqPick} onToggle={toggleFaq}
            moveLabel="Needs an interview instead" onMove={(k) => moveTo(k, 'interview')} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
            <Button variant={foldingIn ? 'outline' : 'primary'} size="sm"
              disabled={!!sending || faqPick.size === 0} onClick={() => send('faq')}>
              {sending === 'faq' ? 'Sending…' : `Send ${faqPick.size} question${faqPick.size === 1 ? '' : 's'} by email`}
            </Button>
            <span className="dash-muted" style={{ fontSize: 12 }}>
              {foldingIn
                ? 'Or leave these — the interview below is set to cover them.'
                : 'One email, all of them together.'}
            </span>
          </div>
        </Card>
      )}

      {ask.interview.length > 0 && (
        <Card padding="lg">
          <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>Needs somebody talking</h3>
          <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
            No one-line answer will do. These become the questions the interview is held to — it covers
            every one and follows up until the answer is specific.
          </p>
          <QuestionList items={ask.interview} picked={ivPick} onToggle={toggleIv}
            moveLabel="Ask as an FAQ instead" onMove={(k) => moveTo(k, 'faq')} />
          {/* Asking twice risks them doing neither. If somebody is sitting down
              for a conversation anyway, the three sentence-long answers cost
              nothing to fold in — so this defaults on whenever there is an
              interview to fold them into. */}
          {ask.faq.length > 0 && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '8px 0 0' }}>
              <input type="checkbox" checked={foldIn} onChange={() => setFoldIn((v) => !v)} style={{ marginTop: 3 }} />
              <span>
                Cover the {faqPick.size} quick question{faqPick.size === 1 ? '' : 's'} in this interview too
                <span className="dash-muted"> — one request instead of two</span>
              </span>
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
            <Button variant="primary" size="sm" disabled={!!sending || ivTotal === 0} onClick={() => send('interview')}>
              {sending === 'interview' ? 'Requesting…' : `Request one interview (${ivTotal})`}
            </Button>
            <span className="dash-muted" style={{ fontSize: 12 }}>One topic, carrying all of them.</span>
          </div>
        </Card>
      )}

      {sent && <p style={{ color: OK, fontSize: 13, margin: 0 }}>{sent}</p>}

      <Card padding="lg">
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Write it</h3>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button type="button" className={`vault-tab ${mode === 'notes' ? 'is-on' : ''}`} onClick={() => setMode('notes')}>
            Copy to paste
          </button>
          <button type="button" className={`vault-tab ${mode === 'file' ? 'is-on' : ''}`} onClick={() => setMode('file')}>
            Rewrite the page
          </button>
        </div>
        <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          {mode === 'notes'
            ? 'Finished copy with exactly where each piece goes. Works on any site — nothing is pushed.'
            : 'Paste the page as it stands and get it back with the additions worked in, and a list of anything that changed.'}
        </p>
        {mode === 'file' && (
          <Textarea rows={6} placeholder="Paste the Astro file or the page's HTML…" value={pasted}
            onChange={(e) => setPasted(e.target.value)} />
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <Button variant="primary" size="sm" disabled={busy || picked.size === 0 || (mode === 'file' && !pasted.trim())}
            onClick={run}>
            {busy ? 'Writing…' : `Add ${picked.size || 'nothing'}${picked.size ? ` to this page` : ''}`}
          </Button>
          {picked.size === 0 && <span className="dash-muted" style={{ fontSize: 12 }}>Tick something above first.</span>}
        </div>
        {err && <p style={{ color: BAD, fontSize: 13 }}>{err}</p>}

        {result?.mode === 'notes' && result.additions && (
          <div style={{ marginTop: 12 }}>
            {result.additions.map((a, i) => (
              <div key={i} style={{ borderTop: '1px solid var(--border, #e5e7eb)', padding: '10px 0' }}>
                {a.heading && <strong style={{ fontSize: 13 }}>{a.heading}</strong>}
                <p style={{ fontSize: 13, whiteSpace: 'pre-wrap', margin: '4px 0' }}>{a.body}</p>
                <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>
                  <Icon name="map-pin" size={12} /> {a.where}
                </p>
              </div>
            ))}
          </div>
        )}

        {result?.mode === 'file' && (
          <div style={{ marginTop: 12 }}>
            {result.truncated && (
              <p style={{ color: BAD, fontSize: 13 }}>The rewrite was cut off before it finished — don&rsquo;t ship this one.</p>
            )}
            <Changes r={result} />
            <Textarea rows={14} value={result.content ?? ''} readOnly />
          </div>
        )}
      </Card>
    </div>
  );
}
